import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestRoot, UTCDate } from 'types';
import { AppContext, WizardContext } from '../control/context';
import { FingerprintsBlob, Features, NullableString } from '../model/features';
import { DatePickerType, FeaturesImpl } from '../model/features-impl';
import { ImageBlob, Enum, MaskedDate, NullableMaskedDate, getTestClassName } from '../model/features-decl';
import { SelectControlComponentOption } from '../select-control/select-control.component';
import { InfoTooltip } from '../info-tooltip/info-tooltip.component';

export type FieldOutType =
  | 'Enum'
  | 'Boolean'
  | 'String'
  | 'NullableString'
  | 'MaskedDate'
  | 'NullableMaskedDate'
  | 'ImageBlob'
  | 'FeatureArray'
  | 'WizardWacomSignature'
  | 'WizardLimeCamera'
  | 'WizardLimeSignature'
  | 'WizardLimeFingerprints';

@Component({
  selector: 'i-field',
  templateUrl: './field.component.html',
  styleUrls: ['./field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FieldComponent implements OnInit, OnDestroy {
  @Input() model: RequestRoot;
  @Input() readonlyWizard: boolean = false;
  @Input() wizard: string;
  @Input() path: string;
  @Input() i18n_name: string;
  @Input() i18n_miss: string;
  @Input() tooltip?: InfoTooltip | string;
  @Input() useRadio = false;
  @Input() useRadioLineBreak = false;
  @Input() useDraft = true;
  @Input() inpMonthFormat: 'letters' | null = null;

  private readonly SUGGESTIONS_COUNT: number = 10;
  private readonly START_SUGGESTING_COUNT: number = 2;

  pathForValidation: string;
  _i18n_name: string;
  _i18n_miss: string;
  multiline: boolean;
  uppercase: boolean;
  readonly: boolean;
  enumOptions: SelectControlComponentOption[] = [];
  datePickerType: DatePickerType;
  maxDate: UTCDate | null;
  keyboardLayoutLanguage: 'en' | 'ru' | null = null;
  debug: boolean = false;

  protected outType: FieldOutType;
  protected paths: string[];
  protected pathsSteps: string[][];
  protected fieldPrototype: any;
  protected listenerId: number;

  constructor(protected appContext: AppContext, private wizardContext: WizardContext) {
    this.debug = appContext.debug();
  }

  visible(outType: FieldOutType): boolean {
    return outType === this.outType && this.visible0();
  }

  private visible0() {
    return undefined !== this.pathsSteps[0].reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), this.model.features);
  }

  set intModel(val) {
    const it = this.pathsSteps[0].slice(0, -1).reduce((pv, cv) => pv[cv], this.model.features);
    it[this.pathsSteps[0].slice(-1)[0]] = this.fieldPrototype.constructor.wrap ? this.fieldPrototype.constructor.wrap(val) : val;
    Features.resetEnumFields(this.model.features, this.wizardContext.getRequestFeaturesTemplate(), this.path);
    FeaturesImpl.updateDefaultValues(this.path, this.model.features);
    Features.updateFieldsVisibility(this.model.features, this.wizardContext.getRequestFeaturesTemplate());
    if (this.useDraft) {
      this.appContext.storeDraft(this.model);
    }
    this.wizardContext.notifyModelListeners();
  }

  readIntModel(index: number): any {
    const val = this.pathsSteps[index].reduce((pv, cv) => pv[cv], this.model.features);
    return this.fieldPrototype.constructor.unwrap ? this.fieldPrototype.constructor.unwrap(val) : val;
  }

  get intModel() {
    return this.readIntModel(0);
  }

  private autocompleteSearchStopper: ReplaySubject<void> | null;
  private lastAutocompleteSource: Observable<string[]> | null;
  private lastAutocompleteResult: string[];
  private stopAutocompleteSearch() {
    if (this.autocompleteSearchStopper) {
      this.autocompleteSearchStopper.next();
      this.autocompleteSearchStopper.complete();
      this.autocompleteSearchStopper = null;
    }
    this.lastAutocompleteSource = null;
    this.lastAutocompleteResult = [];
  }
  get intAutocompleteOptions() {
    if (!this.intModel || this.intModel.length < this.START_SUGGESTING_COUNT) {
      this.stopAutocompleteSearch();
    } else {
      const optionsFunc = FeaturesImpl.getSuggestedOptions(this.path);
      if (!optionsFunc) {
        this.stopAutocompleteSearch();
      } else {
        const autocompleteSource = optionsFunc(this.model.features, this.intModel);
        if (this.lastAutocompleteSource !== autocompleteSource) {
          this.stopAutocompleteSearch();
          this.autocompleteSearchStopper = new ReplaySubject<void>(1);
          this.lastAutocompleteSource = autocompleteSource;
          this.lastAutocompleteSource.pipe(takeUntil(this.autocompleteSearchStopper)).subscribe((r) => {
            this.lastAutocompleteResult = r.slice(0, this.SUGGESTIONS_COUNT);
          });
        }
      }
    }
    return this.lastAutocompleteResult;
  }

  readBlobURL(index: number): string {
    return this.model.readBlobURL(this.paths[index]);
  }

  writeBlob(index: number, value: string | { raw: string; img: string }) {
    if (typeof value === 'string') {
      this.model.writeBlob(this.paths[index], value);
    } else {
      this.model.writeBlob(this.paths[index], value.raw, value.img);
    }
    if (this.useDraft) {
      this.appContext.storeDraft(this.model);
    }
  }

  private parseMaskedDate(val: string | null): string | null {
    return FeaturesImpl.parseMaskedDate(this.path, this.model.features, val, this.appContext.getLocale());
  }
  private formatMaskedDate(val: string | null, monthFormat: 'letters' | null): string | null {
    return FeaturesImpl.formatMaskedDate(null, val, this.appContext.getLocale(), monthFormat);
  }
  parseMaskedDateLambda = (val: string | null) => this.parseMaskedDate(val);
  formatMaskedDateLambda = (val: string | null, monthFormat: 'letters' | null) => this.formatMaskedDate(val, monthFormat);

  set maskedDate(val) {
    const it = this.pathsSteps[0].slice(0, -1).reduce((pv, cv) => pv[cv], this.model.features);
    it[this.pathsSteps[0].slice(-1)[0]].value = this.parseMaskedDate(val);
    Features.resetEnumFields(this.model.features, this.wizardContext.getRequestFeaturesTemplate(), this.path);
    FeaturesImpl.updateDefaultValues(this.path, this.model.features);
    Features.updateFieldsVisibility(this.model.features, this.wizardContext.getRequestFeaturesTemplate());
    if (this.useDraft) {
      this.appContext.storeDraft(this.model);
    }
    this.wizardContext.notifyModelListeners();
  }
  get maskedDate() {
    const val = this.pathsSteps[0].reduce((pv, cv) => pv[cv], this.model.features);
    return this.formatMaskedDate(val.value, this.inpMonthFormat);
  }

  set enumValue(val) {
    this.intModel = val ? val.text : '';
  }
  get enumValue() {
    return this.enumOptions.find((x) => x.text === this.intModel) || null;
  }

  changed() {
    if (this.useDraft) {
      this.appContext.storeDraft(this.model);
    }
  }

  get testClass(): string {
    return getTestClassName(this.path);
  }

  ngOnInit() {
    this.paths = this.path.split(',');
    this.pathForValidation = this.paths[0];
    this.pathsSteps = this.paths.map((x) => x.split('.'));
    if (this.wizard) {
      switch (this.wizard) {
        case 'LimeCamera':
          this.outType = 'WizardLimeCamera';
          this.fieldPrototype = ImageBlob.prototype;
          break;
        case 'LimeSignature':
          this.outType = 'WizardLimeSignature';
          this.fieldPrototype = ImageBlob.prototype;
          break;
        case 'LimeFingerprints':
          this.outType = 'WizardLimeFingerprints';
          this.fieldPrototype = FingerprintsBlob.prototype;
          break;
        case 'WacomSignature':
          this.outType = 'WizardWacomSignature';
          this.fieldPrototype = ImageBlob.prototype;
          break;
      }
    } else {
      this.keyboardLayoutLanguage = Features.getKeyboardLanguage(this.path);
      this.fieldPrototype = FeaturesImpl.findPrototype(
        Object.getPrototypeOf(this.wizardContext.getRequestFeaturesTemplate()),
        this.wizardContext.getRequestFeaturesTemplate(),
        this.pathsSteps[0],
      );
      this.outType = this.fieldPrototype.constructor.name;
      if (this.fieldPrototype instanceof Enum) {
        this.outType = 'Enum';
        this.pathForValidation += '.value';
        const opts = FeaturesImpl.queryEnumOptions(this.path, this.wizardContext.getRequestFeaturesTemplate());
        if (opts && opts.listGenerator) {
          const listGenerator = opts.listGenerator;
          const updater = () => {
            for (const value of listGenerator(this.model.features)) {
              const text = Object.getPrototypeOf(value).constructor.unwrap(value);
              this.enumOptions.push({ key: text, text: text });
            }
          };
          updater();
          this.listenerId = this.wizardContext.registerModelListener(() => {
            this.enumOptions = [];
            updater();
          });
        } else {
          for (const key in this.fieldPrototype.constructor) {
            const value = (this.fieldPrototype.constructor as any)[key];
            if (value instanceof this.fieldPrototype.constructor) {
              const text = Object.getPrototypeOf(value).constructor.unwrap(value);
              this.enumOptions.push({ key: text, text: text });
            }
          }
        }
      } else if (this.fieldPrototype.constructor.prototype === NullableString.prototype) {
        this.outType = 'NullableString';
        this.pathForValidation += '.value';
      } else if (this.fieldPrototype.constructor.prototype === MaskedDate.prototype) {
        this.outType = 'MaskedDate';
        this.pathForValidation += '.value';
        const query = FeaturesImpl.queryMaskedDate(this.path, this.model.features);
        this.datePickerType = query.type;
        this.maxDate = query.maxDate;
      } else if (this.fieldPrototype.constructor.prototype === NullableMaskedDate.prototype) {
        this.outType = 'NullableMaskedDate';
        this.pathForValidation += '.value';
        const query = FeaturesImpl.queryMaskedDate(this.path, this.model.features);
        this.datePickerType = query.type;
        this.maxDate = query.maxDate;
      }
      const strOpts = FeaturesImpl.queryStringOptions(this.path, this.model.features);
      this.multiline = strOpts.multiline;
      this.uppercase = strOpts.uppercase;
      this.readonly = strOpts.readonly;
    }
    this._i18n_name = this.appContext.getI18NString(this.i18n_name);
    this._i18n_miss = this.appContext.getI18NString(this.i18n_miss);
  }

  ngOnDestroy() {
    if (this.listenerId) {
      this.wizardContext.unregisterModelListener(this.listenerId);
    }
    this.stopAutocompleteSearch();
  }
}
